// Pure, view-agnostic helpers for the high-identity-singleton page (SpinSingletonsPage). Kept separate
// from spinMetrics.logic.ts / spinRefDetail.logic.ts because this view asks the MIRROR question of
// reference concordance: not "did SPIN split one reference across clusters?" but "did SPIN leave a
// near-perfect reference match sitting alone?".
import type { HighIdentitySingletonResponse } from '../../../Generated/HighIdentitySingletonResponse.ts'
import type { SpinSizeCount } from '../../../Generated/SpinSizeCount.ts'
import { NEAR_PERFECT_IDENTITY, pct } from './spinRefDetail.logic.js'

/** Backend default for `minHitPercentage` — the same near-perfect floor the reference triage uses. */
export const DEFAULT_SINGLETON_IDENTITY = NEAR_PERFECT_IDENTITY

/**
 * Identity floors offered for the cohort. Deliberately narrow: below ~99% the cohort stops being
 * diagnostic at all, because cluster size and reference identity are strongly correlated on this data
 * (singleton members average 91.8% identity — most singletons simply have weak reference evidence, which
 * is expected and not actionable). Only a near-perfect match makes "and yet it clusters alone" a question.
 */
export const SINGLETON_IDENTITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '99', label: '≥ 99%' },
  { value: String(NEAR_PERFECT_IDENTITY), label: `≥ ${NEAR_PERFECT_IDENTITY}% (near-perfect, default)` },
  { value: '99.9', label: '≥ 99.9%' },
  { value: '100', label: '= 100% (exact)' }
]

/**
 * Parse the identity-floor control into `minHitPercentage`. Unlike the reference page's
 * `parseMinIdentity`, "no floor" is NOT an option here — without a floor the query returns all 291k
 * singletons and the cohort loses its meaning — so junk / empty / out-of-range falls back to the backend
 * default rather than omitting the parameter.
 */
export const parseSingletonIdentity = (raw: string): number => {
  const v = Number(raw.trim())
  if (raw.trim() === '' || !Number.isFinite(v) || v <= 0 || v > 100) return DEFAULT_SINGLETON_IDENTITY
  return v
}

/**
 * Whole-set count of single-member SPINs, read off the (cheap, whole-set) `metrics.sizeDistribution`.
 * This is the denominator that makes the cohort legible: 11,471 high-identity singletons is a scary
 * number alone and a small slice of the 291,058 singletons that actually exist.
 */
export const singletonSpinCount = (dist: SpinSizeCount[] | undefined): number =>
  (dist ?? []).reduce((sum, s) => (s.memberCount === 1 ? sum + s.spinCount : sum), 0)

/** Shares that put the cohort in proportion; each is null when its denominator is unknown or zero. */
export interface SingletonCohortContext {
  /** Cohort size — SPINs with exactly one member matching NCBI at or above the floor. */
  cohort: number
  /** All single-member SPINs in the current set (whole-set, from the size distribution). */
  singletons: number
  /** All SPINs in the current set. */
  spins: number
  /** Cohort as a percentage of all singletons. */
  shareOfSingletons: number | null
  /** Singletons as a percentage of all SPINs — the "67% of clusters are singletons" baseline. */
  singletonShareOfSpins: number | null
  /** Cohort as a percentage of all SPINs. */
  shareOfSpins: number | null
}

export const singletonCohortContext = (
  cohort: number,
  dist: SpinSizeCount[] | undefined,
  spins: number | undefined
): SingletonCohortContext => {
  const singletons = singletonSpinCount(dist)
  const total = spins ?? 0
  const share = (part: number, whole: number): number | null => (whole > 0 ? (part / whole) * 100 : null)
  return {
    cohort,
    singletons,
    spins: total,
    shareOfSingletons: share(cohort, singletons),
    singletonShareOfSpins: share(singletons, total),
    shareOfSpins: share(cohort, total)
  }
}

/**
 * One sentence stating the cohort against both denominators, so the count cannot be read as "SPIN failed
 * 11,471 times out of nothing". Degrades gracefully when the metrics snapshot has not arrived (the
 * cohort loads independently of it, so the denominators may legitimately be missing).
 */
export const cohortSentence = (ctx: SingletonCohortContext, threshold: number): string => {
  const one = ctx.cohort === 1
  const head = `${ctx.cohort.toLocaleString()} SPIN${one ? '' : 's'} hold${one ? 's' : ''} exactly one member OTU that matched an NCBI reference at ≥ ${threshold}%`
  if (ctx.singletons === 0) return `${head}.`
  const ofSingletons = `${pct(ctx.shareOfSingletons)} of the ${ctx.singletons.toLocaleString()} single-member SPINs in this set`
  if (ctx.spins === 0) return `${head} — ${ofSingletons}.`
  return `${head} — ${ofSingletons}, which are themselves ${pct(ctx.singletonShareOfSpins)} of all ${ctx.spins.toLocaleString()} current SPINs.`
}

/**
 * Truncation disclosure. `limit` caps the list server-side while `totalCount` keeps the true cohort
 * size, so a capped table rendered as if complete is the exact failure this page must not ship. Returns
 * null only when the whole cohort is on screen. Also fires on `returnedCount < totalCount` even if the
 * backend forgot to set `truncated`.
 */
export const singletonTruncationNote = (
  r: Pick<HighIdentitySingletonResponse, 'truncated' | 'returnedCount' | 'totalCount'>
): string | null => {
  if (!r.truncated && r.returnedCount >= r.totalCount) return null
  return `Showing ${r.returnedCount.toLocaleString()} of ${r.totalCount.toLocaleString()}, highest abundance first — the list is capped.`
}
